/* ============================================================
   RealU Online — shared form schema, scoring + career data.
   TypeScript port of the design prototype's form-data.js — ONE
   source of truth for questions, weights, and the RealU Score.

   SAMPLE career/job data lives here, marked clearly. In Phase 2 it
   is replaced by the real region Industries/Jobs from the backend.
   ============================================================ */
import type { IconName } from './icons';

export interface Option {
  value: string;
  label: string;
  emoji?: string;
  note?: string;
}

export interface HabitOption {
  label: string;
  points: number;
}

export interface Habit {
  id: string;
  title: string;
  prompt: string;
  options: HabitOption[];
}

export interface Job {
  id?: string;
  title: string;
  req: number;
  salary: number;
}

export interface Category {
  id: string;
  label: string;
  icon: IconName;
  skills: string;
  lockedHint?: string;
  jobs: Job[];
}

export interface Answers {
  accessCode: string;
  eventId: string;
  firstName: string;
  lastName: string;
  gender: string;
  teacherId: string;
  sessionId: string;
  gpa: number | null;
  habits: Record<string, number>;
  careerCategory: string;
  careerCategories: string[];
  career: Job | null;
  maritalStatus: string;
  kidsCount: number | null;
  kidsAges: number[];
  pets: string[];
  // transient UI flags
  _eventLoaded?: boolean;
  _petsAnswered?: boolean;
  _careerExpandAll?: boolean;
  _careerQualOnly?: boolean;
}

// ---- Event groups & sessions ---------------------------------------------
// Students pick their GROUP (a teacher's class); the session is derived from it
// when the group attends one session, or asked when it attends several. Real
// events inject these via applyGroupsConfig (ids are DB ids); the sample below
// is preview-only and exercises both paths.
export interface SessionOption {
  id: string;
  label: string;
}

export interface Group {
  id: string;
  label: string;
  sessions: SessionOption[];
}

export let GROUPS: Group[] = [
  {
    id: 'g1',
    label: 'Mrs. Rivera — 2nd Period',
    sessions: [{ id: 's1', label: 'Morning Session — 9:00 AM' }],
  },
  {
    id: 'g2',
    label: 'Mr. Chen — 4th Period',
    sessions: [
      { id: 's1', label: 'Morning Session — 9:00 AM' },
      { id: 's2', label: 'Afternoon Session — 12:30 PM' },
    ],
  },
];

export function applyGroupsConfig(groups: Group[] | null | undefined): void {
  if (Array.isArray(groups) && groups.length) GROUPS = groups;
}

// Event identity, set when the page was deep-linked with a valid ?access_code
// (the event-code step is then pre-answered) or after the student's code checks
// out. blankAnswers() seeds from it.
export let EVENT_INFO: { eventId: string; accessCode: string } | null = null;

export function applyEventInfo(info: { id?: string; accessCode?: string } | null | undefined): void {
  if (info && info.id) EVENT_INFO = { eventId: info.id + '', accessCode: info.accessCode || '' };
}

// Friendly message when a deep-linked ?access_code failed the event gate —
// surfaced by the event-code step on first render.
export let EVENT_ERROR = '';

export function applyEventError(err: { message?: string } | null | undefined): void {
  if (err && err.message) EVENT_ERROR = err.message;
}

// Gender options are injected from the server config (lib/realuSurveyConfig.js);
// applyGenderConfig() fills this before mount — no hardcoded default here.
export let GENDERS: Option[] = [];

// ---- The 8 RealU "habit" questions --------------------------------------
// Each answer carries points on a consistent A–E scale (.5/.4/.3/.15/0)
// so the score is defensible and easy to tune.
export let HABITS: Habit[] = [];

// Overwrite HABITS from the server-injected config. The server already merged the
// default scoring points into each answer (admin point edits are ignored), so this
// just adopts the list as-is. Empty/invalid input leaves the current HABITS untouched.
export function applyHabitConfig(configHabits: Habit[] | null | undefined): void {
  if (Array.isArray(configHabits) && configHabits.length) HABITS = configHabits;
}

// Overwrite GENDERS from server config (admin toggles whether "Prefer not to say"
// is included). Empty/invalid input leaves the current GENDERS untouched.
export function applyGenderConfig(genders: Option[] | null | undefined): void {
  if (Array.isArray(genders)) GENDERS = genders;
}

// Life-intro step copy — injected from server config (no hardcoded default here).
export let LIFE_INTRO: { title: string; subtitle: string } = { title: '', subtitle: '' };

export function applyLifeIntroConfig(li: { title?: string; subtitle?: string } | null | undefined): void {
  if (li && typeof li === 'object') {
    LIFE_INTRO = { title: li.title || '', subtitle: li.subtitle || '' };
  }
}

// Marital step — only the "Married" option label is editable (server-injected).
export let MARITAL: { marriedLabel: string; prompt: string } = { marriedLabel: '', prompt: '' };

export function applyMaritalConfig(
  marital: { marriedLabel?: string; prompt?: string } | null | undefined
): void {
  if (marital && typeof marital.marriedLabel === 'string') {
    MARITAL = { marriedLabel: marital.marriedLabel, prompt: marital.prompt || '' };
  }
}

// ---- Career categories + sample jobs ------------------------------------
// SAMPLE — replaced by real region Industries/Jobs in Phase 2.
// Each job has a RealU Score requirement (req) and an annual salary.
export let CATEGORIES: Category[] = [
  {
    id: 'creative',
    label: 'Creative & Design',
    icon: 'palette',
    skills: 'Innovation, visual design, storytelling, imagination',
    lockedHint:
      'Creative careers reward big imagination and steady follow-through. Keep finishing your projects and sharing your ideas. Those habits will open this door.',
    jobs: [
      { title: 'Animator', req: 6.5, salary: 61692 },
      { title: 'Architect', req: 6.0, salary: 59492 },
      { title: 'Executive Chef', req: 6.0, salary: 53496 },
      { title: 'Creative Assistant', req: 0, salary: 28600 },
    ],
  },
  {
    id: 'leadership',
    label: 'Leadership & Decision-Making',
    icon: 'flag',
    skills: 'Team management, decisions, problem solving under pressure',
    lockedHint:
      "Leaders earn trust by showing up and following through. Keep being reliable and helping your team. You'll grow into roles like these.",
    jobs: [
      { title: 'Operations Manager', req: 6.8, salary: 71540 },
      { title: 'Restaurant Owner', req: 6.0, salary: 58200 },
      { title: 'Site Supervisor', req: 5.2, salary: 52100 },
      { title: 'Shift Lead Trainee', req: 0, salary: 31200 },
    ],
  },
  {
    id: 'people',
    label: 'People & Communication',
    icon: 'chat',
    skills: 'Active listening, public speaking, empathy, customer service',
    lockedHint:
      'These careers are all about people. Keep practicing kindness, listening well, and speaking up. Those habits unlock this path.',
    jobs: [
      { title: 'School Counselor', req: 6.6, salary: 60140 },
      { title: 'Nurse', req: 6.2, salary: 64900 },
      { title: 'Sales Representative', req: 5.0, salary: 48700 },
      { title: 'Customer Service Rep', req: 0, salary: 29900 },
    ],
  },
  {
    id: 'hands',
    label: 'Hands-On & Mechanical',
    icon: 'wrench',
    skills: 'Repairing, building, operating tools or machinery',
    lockedHint:
      'Hands-on pros are precise and dependable. Keep being careful with your work and on time. Those skills build toward these jobs.',
    jobs: [
      { title: 'Electrician', req: 5.6, salary: 56900 },
      { title: 'Auto Technician', req: 4.8, salary: 47300 },
      { title: 'HVAC Installer', req: 5.2, salary: 51200 },
      { title: 'General Laborer', req: 0, salary: 31800 },
    ],
  },
  {
    id: 'math',
    label: 'Math & Financial',
    icon: 'calc',
    skills: 'Budgeting, calculations, financial planning',
    lockedHint:
      "Money careers reward focus and good habits. Keep up your math and stay organized. You're building the skills for this field.",
    jobs: [
      { title: 'Accountant', req: 6.6, salary: 65300 },
      { title: 'Loan Officer', req: 5.8, salary: 58100 },
      { title: 'Bank Teller', req: 4.6, salary: 39400 },
      { title: 'Cashier', req: 0, salary: 27300 },
    ],
  },
  {
    id: 'science',
    label: 'Science & Problem-Solving',
    icon: 'flask',
    skills: 'Scientific reasoning, research, technical writing',
    lockedHint:
      "Science takes curiosity and grit. Keep asking questions and pushing through hard problems. That's exactly what unlocks this work.",
    jobs: [
      { title: 'Lab Scientist', req: 7.0, salary: 68800 },
      { title: 'Pharmacy Tech', req: 5.8, salary: 44600 },
      { title: 'Field Researcher', req: 6.0, salary: 55200 },
      { title: 'Lab Assistant', req: 0, salary: 30400 },
    ],
  },
  {
    id: 'organized',
    label: 'Organized & Detail-Oriented',
    icon: 'clipboard',
    skills: 'Time management, accuracy, task coordination',
    lockedHint:
      "These roles love people who stay organized and meet deadlines. Keep managing your time well. You're on your way.",
    jobs: [
      { title: 'Project Coordinator', req: 6.2, salary: 57400 },
      { title: 'Office Manager', req: 5.4, salary: 49800 },
      { title: 'Logistics Planner', req: 5.8, salary: 53600 },
      { title: 'Data Entry Clerk', req: 0, salary: 28100 },
    ],
  },
  {
    id: 'technical',
    label: 'Technical & Analytical',
    icon: 'chip',
    skills: 'Data analysis, troubleshooting, precision, computer literacy',
    lockedHint:
      'Tech careers reward problem-solvers who don’t give up. Keep building your focus and finishing tough work. This field is within reach.',
    jobs: [
      { title: 'Software Developer', req: 7.2, salary: 78900 },
      { title: 'IT Support Specialist', req: 5.6, salary: 52400 },
      { title: 'Data Analyst', req: 6.6, salary: 67100 },
      { title: 'Help Desk Assistant', req: 0, salary: 32500 },
    ],
  },
];

// Overwrite CATEGORIES from the server-injected config (the event region's real
// industries+jobs). Empty/invalid input keeps the sample categories above, so
// /realu-next without an access_code still previews with sample careers.
export function applyCategoriesConfig(cats: Category[] | null | undefined): void {
  if (Array.isArray(cats) && cats.length) CATEGORIES = cats;
}

// ---- Scoring -------------------------------------------------------------
// habitTotal: sum of the 8 answers, each up to .5 -> 0..4.0
// RealU Score = GPA (base, 0..4) + habit total (0..4), capped at 8.0.
export function habitTotal(habits: Record<string, number>): number {
  return HABITS.reduce(
    (sum, q) => {
      const idx = habits[q.id];
      if (typeof idx !== 'number') return sum;
      return sum + (q.options[idx]?.points ?? 0);
    },
    0
  );
}

export function realUScore(gpa: number | null, habits: Record<string, number>): number {
  const g = typeof gpa === 'number' ? gpa : 0;
  const h = habitTotal(habits); // 0..4
  return Math.min(Math.round((g + h) * 100) / 100, 8.0); // 0..8, 2 decimals
}

// Job offers earned, scaled with the score.
export function jobOffers(score: number): number {
  if (score >= 7) return 5;
  if (score >= 6) return 4;
  if (score >= 5) return 3;
  if (score >= 4) return 2;
  return 1;
}

// A short, encouraging readout for the reveal screen.
export function scoreBlurb(score: number): string {
  if (score >= 7) return 'Amazing! Employers love students like you. Pick any job you want.';
  if (score >= 6) return 'Great work! Your habits unlocked a lot of cool jobs.';
  if (score >= 5) return 'Nice. A few better habits will unlock even more jobs.';
  if (score >= 4) return 'Good start. Small changes open up more jobs.';
  return 'Everyone starts somewhere. Better habits mean more jobs.';
}

// default empty answer object — pre-answers the event-code step when the page
// was deep-linked with a valid ?access_code (EVENT_INFO set before mount)
export function blankAnswers(): Answers {
  return {
    accessCode: EVENT_INFO ? EVENT_INFO.accessCode : '',
    eventId: EVENT_INFO ? EVENT_INFO.eventId : '',
    _eventLoaded: !!EVENT_INFO,
    firstName: '',
    lastName: '',
    gender: '',
    teacherId: '',
    sessionId: '',
    gpa: null,
    habits: {},
    careerCategory: '',
    careerCategories: [],
    career: null,
    maritalStatus: '',
    kidsCount: null,
    kidsAges: [],
    pets: [],
  };
}
